<template>
    <nav class="d-sm-flex">
        <ul v-if="showList && value.total_pages > 1" class="pagination align-self-center mb-0">

            <!-- First -->
            <li class="page-item" v-if="canGoFirst">
                <a class="page-link" href="#" @click.prevent="firstPage" :aria-label="$t('pagination.first')">
                    <span aria-hidden="true">&laquo;</span>
                </a>
            </li>

            <!-- Prev -->
            <li class="page-item" v-if="canGoPrevious">
                <a class="page-link" href="#" @click.prevent="previousPage" :aria-label="$t('pagination.previous')">
                    <span aria-hidden="true">&lsaquo;</span>
                </a>
            </li>

            <!-- Range -->
            <li v-for="(page, index) in pages"
                class="page-item"
                :class="{
                    'active': page == value.current_page || (!Number.isInteger(page) && isPageListShown[index]),
                    'position-relative': !Number.isInteger(page)
                }">
                <template v-if="!Number.isInteger(page)">
                    <a href="#"
                       class="page-link"
                       @click.prevent="handleTogglePageList(index)">{{ page }}</a>
                    <div class="page-list-wrapper" :class="{ 'is--active': isPageListShown[index] }">
                        <ul class="page-list" :ref="`pageList${index}`">
                            <li v-for="pageList in pageListFiltered"
                                :key="pageList"
                                class="page-list-item"
                                :class="{ 'active': pageList == value.current_page }">
                                <a href="#" class="page-list-link" @click.prevent="goToPage(pageList)">{{ pageList }}</a>
                            </li>
                        </ul>
                        <a href="#" class="page-list-arrow is--up" @click.prevent="scrollPageListUp(`pageList${index}`)">
                            <Icon icon="angle-down" />
                        </a>
                        <a href="#" class="page-list-arrow is--down" @click.prevent="scrollPageListDown(`pageList${index}`)">
                            <Icon icon="angle-down" />
                        </a>
                    </div>
                    <span class="page-list-wrapper-arrow" :class="{ 'is--active': isPageListShown[index] }" aria-hidden="true"></span>
                </template>
                <a v-else href="#" class="page-link" @click.prevent="goToPage(page)">{{ page }}</a>
            </li>

            <!-- Next -->
            <li class="page-item" v-if="canGoNext">
                <a class="page-link" href="#" @click.prevent="nextPage" :aria-label="$t('pagination.next')">
                    <span aria-hidden="true">&rsaquo;</span>
                </a>
            </li>

            <!-- Last -->
            <li class="page-item" v-if="canGoLast">
                <a class="page-link" href="#" @click.prevent="lastPage" :aria-label="$t('pagination.last')">
                    <span aria-hidden="true">&raquo;</span>
                </a>
            </li>
        </ul>

        <!-- Jump to specific page -->
        <div v-if="showJumpToPage" class="align-self-center" :class="{ 'ms-sm-2 mt-2 mt-sm-0': showList }">
            <input v-model="specificPage" class="form-control specific-page border" :class="{ 'is-invalid': !isSpecificPageValid }" type="number" placeholder="Jump to page" :min="1" :max="maxSpecificPage" @keypress.enter.prevent="goToPage(specificPage)" />
        </div>
    </nav>
</template>

<script>
    export default {
        name: 'Pagination',
        data() {
            return {
                specificPage: null,
                isPageListShown: []
            }
        },
        props: {
            showList: {
                type: Boolean,
                default: false
            },
            value: {
                type: Object,
                required: true
            }
        },
        methods: {
            previousPage() {
                let previousPage = this.value.current_page - 1
                previousPage = Math.max(1, previousPage)
                this.goToPage(previousPage)
            },
            nextPage() {
                let nextPage = this.value.current_page + 1
                nextPage = Math.min(this.value.total_pages, nextPage)
                this.goToPage(nextPage)
            },
            firstPage() {
                this.goToPage(1)
            },
            lastPage() {
                this.goToPage(this.value.total_pages)
            },
            goToPage(page) {
                this.isPageListShown = []
                this.value.current_page = page
                this.$emit('input', this.value)
            },
            range(c, m) {
                var current = c,
                    last = m,
                    delta = 2,
                    left = current - delta,
                    right = current + delta + 1,
                    range = [],
                    rangeWithDots = [],
                    l;

                for (let i = 1; i <= last; i++) {
                    if (i == 1 || i == last || i >= left && i < right) {
                        range.push(i);
                    }
                }

                for (let i of range) {
                    if (l) {
                        if (i - l === 2) {
                            rangeWithDots.push(l + 1);
                        } else if (i - l !== 1) {
                            rangeWithDots.push('…');
                        }
                    }
                    rangeWithDots.push(i);
                    l = i;
                }

                return rangeWithDots;
            },
            handleTogglePageList(index) {
                if(!this.isPageListShown[index]) {
                    this.isPageListShown = Array(index + 1).fill(false)
                }
                this.$set(this.isPageListShown, index, !this.isPageListShown[index])
            },
            scrollPageListDown(refIndex) {
                this.$refs[refIndex][0].scrollTop += 50
            },
            scrollPageListUp(refIndex) {
                this.$refs[refIndex][0].scrollTop -= 50
            }
        },
        computed: {
            canGoFirst: function () {
                return this.canGoPrevious
            },
            canGoPrevious: function () {
                return this.value.current_page > 1
            },
            canGoNext: function () {
                return this.value.current_page < this.value.total_pages
            },
            canGoLast: function () {
                return this.value.total_pages > this.value.current_page + 1
            },
            isSpecificPageValid: function() {
                if(!this.specificPage) return true
                return this.specificPage >= 1 && (typeof this.maxSpecificPage === 'undefined' || this.specificPage <= this.maxSpecificPage)
            },
            maxSpecificPage: function () {
                return this.value.total > 0 ? this.value.total_pages : undefined
            },
            pageListFiltered: function() {
                let totalPages = this.value.total_pages
                // Limit the list of pages available for quick access
                if(totalPages >= 50) {
                    let maxListItems = 10
                    let coeff = totalPages / maxListItems
                    totalPages = [1]
                    for(let i = 1; i <= maxListItems; i++) {
                        totalPages.push(parseInt(i * coeff))
                    }
                }
                return totalPages
            },
            pages: function () {
                return this.range(this.value.current_page, this.value.total_pages)
            },
            showJumpToPage: function () {
                return !(this.value.current_page === 1 && this.value.total_pages === 1)
            }
        }
    }
</script>

<style scoped>
    .specific-page { width: 174px; }
</style>